import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckLg } from "../../assets/icons/bootstrap/CheckLg";
import { Files } from "../../assets/icons/bootstrap/Files";
import useCopyLink from "../../hooks/useCopyLink";
import {
    buildAndroidBrowserIntent,
    dismissCheckoutRecovery,
    isAndroid,
    subscribeToCheckoutRecovery,
} from "../../utils/checkoutRecovery";
import styles from "./CheckoutRecovery.module.scss";

const RecoveryPanel = ({ checkoutUrl }: { checkoutUrl: string }) => {
    const { t } = useTranslation();
    const { copy, copied } = useCopyLink(checkoutUrl);
    const intentUrl = isAndroid()
        ? buildAndroidBrowserIntent(checkoutUrl)
        : null;
    const panelRef = useRef<HTMLDialogElement>(null);

    // A native dialog gives true modal semantics — inert background, focus
    // placement, Escape to close. WebViews too old for showModal still get
    // the panel, just shown non-modally via the open attribute.
    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) {
            return;
        }
        if (typeof panel.showModal === "function") {
            panel.showModal();
        } else {
            panel.setAttribute("open", "");
        }
    }, []);

    return (
        <dialog
            ref={panelRef}
            className={styles.panel}
            data-testid="checkout-recovery"
            aria-labelledby="checkout-recovery-heading"
            onCancel={dismissCheckoutRecovery}
        >
            <div className={styles.inner}>
                <button
                    type="button"
                    aria-label={t("general.close")}
                    className={styles.close}
                    onClick={dismissCheckoutRecovery}
                >
                    &times;
                </button>
                <h2 id="checkout-recovery-heading" className={styles.heading}>
                    {t("checkout_recovery.heading")}
                </h2>
                <p className={styles.body}>{t("checkout_recovery.body")}</p>

                {intentUrl && (
                    <a className={styles.primary} href={intentUrl}>
                        {t("checkout_recovery.open_in_browser")}
                    </a>
                )}

                <p className={styles.urlLabel}>
                    {t("checkout_recovery.link_label")}
                </p>
                {/* Selectable text is the real floor here: it still works when
                    the clipboard API is missing or permission-gated. */}
                <p className={styles.url}>{checkoutUrl}</p>

                <button
                    type="button"
                    data-testid="copy-checkout-link"
                    className={styles.copy}
                    onClick={copy}
                >
                    <span className={styles.copyIcon} aria-hidden="true">
                        {copied ? <CheckLg /> : <Files />}
                    </span>
                    {copied ? t("general.copied") : t("general.copy_link")}
                </button>
            </div>
        </dialog>
    );
};

/**
 * Shown when a payment navigation did not take the buyer anywhere — see
 * navigateToCheckout. Mounted once for the whole app, since any campaign flow
 * can start a payment.
 */
const CheckoutRecovery = () => {
    const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

    useEffect(() => subscribeToCheckoutRecovery(setCheckoutUrl), []);

    if (!checkoutUrl) {
        return null;
    }
    return <RecoveryPanel checkoutUrl={checkoutUrl} />;
};

export default CheckoutRecovery;
